import { enqueueSnackbar } from "notistack";

import analytics from "fond/analytics";
import { apiSlice } from "fond/api";
import { useDeleteLayersByKey } from "fond/hooks/useDeleteLayersByKey";
import { inputLayerGroups } from "fond/layers";
import { beginEditing, clearLayers, EditMode, repositionHubs, revertHubs, setEditMode } from "fond/project/redux";
import { LayerIds } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

interface UseDesignRefinementActionsReturn {
  /**
   * Enter hub moving mode.
   *
   * The `resetDropAssignments` argument can be used to reset any existing drop assignments when entering
   * this mode. Errors are reported to the user.
   */
  moveHubs: (options: { resetDropAssignments: boolean }) => Promise<void>;
  /**
   * Begin editing drop assignments.
   */
  editDropAssignments: () => void;
  /**
   * Revert hubs to their original locations.
   */
  deleteHubs: () => Promise<void>;
  /**
   * Delete existing drop assignments.
   */
  deleteDropAssignments: () => Promise<void>;
  /**
   * Delete blockers.
   */
  deleteBlockers: () => Promise<void>;
  /**
   * Reset all design edits. Errors are reported to the user.
   */
  resetEdits: () => Promise<void>;
}

/**
 * Actions for refining a completed design.
 */
export const useDesignRefinementActions = (): UseDesignRefinementActionsReturn => {
  const { open } = useStackedNavigationContext();
  const dispatch = useAppDispatch();

  const versionId = useAppSelector((state) => state.project.versionId);
  const deleteLayersByKey = useDeleteLayersByKey(versionId);

  const deleteDesignLayers = async (layerIds: string[]) => {
    await deleteLayersByKey(layerIds);
    dispatch(clearLayers(layerIds));
    dispatch(apiSlice.util.invalidateTags([{ type: "VersionStatus", id: versionId }]));
  };

  const moveHubs = async ({ resetDropAssignments }: { resetDropAssignments: boolean }) => {
    if (resetDropAssignments) {
      try {
        await deleteDesignLayers([LayerIds.dropAssignment]);
      } catch {
        enqueueSnackbar("Failed to reset drop assignments.", { variant: "error" });
        return;
      }
    }
    dispatch(repositionHubs());
    open("draw");
  };

  const editDropAssignments = () => {
    analytics.track("Clicked edit drop assignments");
    dispatch(beginEditing(inputLayerGroups.dropAssignments.id));
    dispatch(setEditMode(EditMode.edit));
    open("draw");
  };

  const deleteHubs = async () => {
    analytics.track("Clicked delete hubs");
    await dispatch(revertHubs());
    enqueueSnackbar("Hubs reverted.", { variant: "success" });
  };

  const deleteDropAssignments = async () => {
    analytics.track("Clicked delete drop assignments");
    await deleteDesignLayers([LayerIds.dropAssignment]);
    enqueueSnackbar("Drop assignments deleted.", { variant: "success" });
  };

  const deleteBlockers = async () => {
    analytics.track("Clicked delete blockers");
    await deleteDesignLayers([LayerIds.blocker]);
    enqueueSnackbar("Blockers deleted.", { variant: "success" });
  };

  const resetEdits = async () => {
    analytics.track("Clicked reset moved equipment");
    try {
      await Promise.all([dispatch(revertHubs()), deleteDesignLayers([LayerIds.dropAssignment, LayerIds.blocker])]);
      enqueueSnackbar("Design edits reset.", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to reset design edits.", { variant: "error" });
    }
  };

  return { moveHubs, editDropAssignments, deleteHubs, deleteDropAssignments, deleteBlockers, resetEdits };
};
